/**
 * Entity <-> Postgres row mapping for server/supabase/migrations/20261102000002_tables.sql. Pure data, no I/O, so it is
 * shared by the browser adapter, the Edge Functions (synced copy) and the seed generator.
 *
 * Rules: camelCase <-> snake_case; instants stay ISO strings in the app and timestamptz in Postgres; nested objects are jsonb;
 * optional fields are `null` in Postgres and ABSENT in the entity (and an absent optional field is written as null, so clearing works).
 * `mode: 'client'` (an owner's browser, RLS) never writes server-controlled columns (plan, trial, deletion, tokens...).
 */
import type { ListFilter } from '../../domain/tx';
import type { ConsentRecord, EntityMap, EntityName } from '../../domain/types';
import type { Row } from './rest';

/** Seals manage/offer tokens: sha256 for lookups, AES-GCM for re-sending links. Implemented server side only (node:crypto). */
export interface TokenVault {
  hash(token: string): string;
  seal(token: string): { hash: string; enc: string };
  open(enc: string): string;
}
export interface MapCtx {
  mode: 'client' | 'service';
  /** Present in service mode only; owners' browsers never see tokens. */
  tokens?: TokenVault;
  /** Public URL of a brand photo object (bucket brand-photos). */
  photoUrl?: (storagePath: string) => string;
}

type Kind = 's' | 'n' | 'b' | 't' | 'j' | 'a';
/** `def`: the column is NOT NULL with a default, so "absent" is written as that default (never null) and read back as absent. */
interface F { k: string; c: string; t: Kind; opt?: true; srv?: true; def?: unknown }
type Flags = { opt?: true; srv?: true; def?: unknown };
const f = (k: string, c: string, t: Kind = 's', flags: Flags = {}): F => ({ k, c, t, ...flags });
const snake = (k: string): string => k.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
/** Same-name field: camelCase key, snake_case column. */
const g = (k: string, t: Kind = 's', flags: Flags = {}): F => f(k, snake(k), t, flags);

const COMMON: F[] = [g('id'), g('createdAt', 't'), g('updatedAt', 't')];
const TENANT: F[] = [...COMMON, g('businessId')];

export const TABLE: Record<EntityName, string> = {
  businesses: 'businesses', owners: 'owners', staff: 'staff', services: 'services', weeklyHours: 'weekly_hours', timeOff: 'time_off',
  clients: 'clients', appointments: 'appointments', waitlistEntries: 'waitlist_entries', waitlistOffers: 'waitlist_offers',
  consents: 'consents', messages: 'messages', payments: 'payments', settings: 'settings',
};
/** Where writes go: consents are an append-only ledger, everything else writes its own table. */
export const WRITE_TABLE: Record<EntityName, string> = { ...TABLE, consents: 'consent_ledger' };

const SPECS: Record<Exclude<EntityName, 'businesses'>, F[]> & { businesses: F[] } = {
  businesses: [
    ...COMMON, g('slug'), g('name'), g('profession'), g('tz'), g('locale'), g('brand', 'j'),
    g('plan', 's', { srv: true }), g('trialStartedAt', 't', { srv: true }), g('topUpMessages', 'n', { srv: true }),
    g('about', 's', { opt: true }), g('whatsapp', 's', { opt: true }), g('phone', 's', { opt: true }), g('address', 's', { opt: true }),
    g('instagram', 's', { opt: true }), g('isDemo', 'b', { opt: true, srv: true, def: false }),
  ],
  owners: [...TENANT, g('phone'), g('name')],
  staff: [...TENANT, g('name'), g('active', 'b'), g('colorKey', 'n'), g('sortOrder', 'n'), g('phone', 's', { opt: true })],
  services: [
    ...TENANT, g('name'), g('category'), g('durationMin', 'n'), g('bufferMin', 'n'), g('priceAgorot', 'n'), g('depositAgorot', 'n', { opt: true }),
    g('staffIds', 'a'), g('active', 'b'), g('sortOrder', 'n'), g('description', 's', { opt: true }),
  ],
  weeklyHours: [...TENANT, g('staffId'), g('weekday', 'n'), g('ranges', 'j')],
  timeOff: [...TENANT, g('staffId', 's', { opt: true }), f('start', 'start_at', 't'), f('end', 'end_at', 't'), g('reason')],
  clients: [...TENANT, g('name'), g('phone'), g('workNotes'), g('sensitiveNotes'), g('tags', 'a'), g('deletedAt', 't', { opt: true })],
  appointments: [
    ...TENANT, g('clientId'), g('serviceId'), g('staffId'), f('start', 'start_at', 't'), f('end', 'end_at', 't'), g('blockedUntil', 't'),
    g('status'), g('source'), g('priceAgorot', 'n'), g('depositAgorot', 'n'), g('depositStatus'), g('depositMethod', 's', { opt: true }),
    g('depositClaimedAt', 't', { opt: true }), g('payment', 'j', { opt: true }), g('paymentId', 's', { opt: true }), g('holdExpiresAt', 't', { opt: true }),
    g('cancelledAt', 't', { opt: true }), g('cancelReason', 's', { opt: true }), g('paidAt', 't', { opt: true }), g('paidAgorot', 'n', { opt: true }),
    g('feeAgorot', 'n', { opt: true }), g('previousStart', 't', { opt: true }), g('note', 's', { opt: true }),
  ],
  waitlistEntries: [...TENANT, g('clientId'), g('serviceId'), g('staffId', 's', { opt: true }), g('days', 'a'), g('daypart'), g('status'), g('appointmentId', 's', { opt: true })],
  waitlistOffers: [
    ...TENANT, g('entryId'), g('clientId'), g('serviceId'), g('staffId'), g('slotStart', 't'), g('slotEnd', 't'), g('slotKey'),
    g('expiresAt', 't'), g('status'), g('appointmentId', 's', { opt: true }),
  ],
  consents: [
    ...TENANT, g('clientId'), g('channel'), g('purpose'), g('wordingVersion'), g('grantedAt', 't'), g('revokedAt', 't', { opt: true }),
    g('source'), g('revokedSource', 's', { opt: true }),
  ],
  messages: [
    ...TENANT, g('channel'), g('template'), f('to', 'to_phone'), g('params', 'j'), g('status'), g('suppressionReason', 's', { opt: true }),
    g('scheduledFor', 't'), g('sentAt', 't', { opt: true }), g('deliveredAt', 't', { opt: true }), g('appointmentId', 's', { opt: true }),
    g('clientId', 's', { opt: true }), g('purpose'), g('dedupeKey'), g('text', 's', { opt: true }), g('manual', 'b', { opt: true, def: false }),
  ],
  payments: [
    ...TENANT, g('appointmentId'), g('amountAgorot', 'n'), g('refundedAgorot', 'n'), g('status'), g('gateway'), g('expiresAt', 't'), g('paidAt', 't', { opt: true }),
    // gateway bookkeeping, not part of the Payment type: carried through so refunds can find the charge at the owner's gateway
    g('gatewayRef', 's', { opt: true, srv: true }),
  ],
  settings: [
    ...TENANT, g('cancelWindowH', 'n'), g('lateCancelFee', 'j'), g('noShowFee', 'j'), g('rescheduleWindowH', 'n'), g('leadTimeMin', 'n'), g('maxAdvanceDays', 'n'),
    g('slotStepMin', 'n'), g('reminderOffsetH', 'n'), g('remindersEnabled', 'b'), g('quietHours', 'j'), g('approvalMode'), g('depositHoldMin', 'n'),
    g('paymentMethods', 'j', { opt: true, def: { cash: true, card: true, bit: { enabled: false }, paybox: { enabled: false } } }),
  ],
};
// the businesses table has no business_id column: businessId mirrors id
const specOf = (entity: EntityName): F[] => (entity === 'businesses' ? SPECS.businesses : SPECS[entity]);

const DELETION_COLS = ['deletion_requested_at', 'deletion_scheduled_for', 'deletion_cancelled_appointments', 'deletion_notified_clients'];
const BUSINESS_EMBEDS = 'slug_aliases(slug,until),brand_photos(id,storage_path,alt,sort_order)';
const TOKEN_ENTITIES: Partial<Record<EntityName, { key: string; hash: string; enc: string }>> = {
  appointments: { key: 'manageToken', hash: 'manage_token_hash', enc: 'manage_token_enc' },
  waitlistOffers: { key: 'token', hash: 'token_hash', enc: 'token_enc' },
};

const iso = (v: unknown): string => new Date(v as string).toISOString();

/** Explicit column list for `select=`: owners cannot `select *` on token tables (column-level grants). */
export function selectList(entity: EntityName, ctx: MapCtx): string {
  const cols = specOf(entity).filter((s) => !(entity === 'businesses' && s.k === 'businessId')).map((s) => s.c);
  if (entity === 'consents') return cols.join(',');
  const extra: string[] = [];
  const tok = TOKEN_ENTITIES[entity];
  if (tok && ctx.tokens) extra.push(tok.hash, tok.enc);
  if (entity === 'businesses') extra.push(...DELETION_COLS, BUSINESS_EMBEDS);
  return [...cols, ...extra].join(',');
}

export function fromRow<E extends EntityName>(entity: E, row: Row, ctx: MapCtx): EntityMap[E] {
  const out: Record<string, unknown> = {};
  for (const s of specOf(entity)) {
    if (entity === 'businesses' && s.k === 'businessId') continue;
    const v = row[s.c];
    if (v === null || v === undefined) {
      if (s.opt) continue;
      out[s.k] = s.t === 'a' ? [] : s.t === 'j' ? {} : s.t === 'b' ? false : s.t === 'n' ? 0 : '';
      continue;
    }
    if (s.opt && 'def' in s && JSON.stringify(v) === JSON.stringify(s.def)) continue;   // the default means "not set"
    out[s.k] = s.t === 't' ? iso(v) : s.t === 'n' ? Number(v) : s.t === 'a' || s.t === 'j' ? v : v;
  }
  if (entity === 'businesses' || entity === 'settings') out.businessId = out.id;
  if (entity === 'consents') { out.channel = 'whatsapp'; out.revokedAt = row.revoked_at ? iso(row.revoked_at) : null; }
  const tok = TOKEN_ENTITIES[entity];
  if (tok) out[tok.key] = ctx.tokens && typeof row[tok.enc] === 'string' ? ctx.tokens.open(row[tok.enc] as string) : '';
  if (entity === 'messages') out.mock = true;
  if (entity === 'businesses') {
    const aliases = (row.slug_aliases as Array<{ slug: string; until: string }> | undefined) ?? [];
    if (aliases.length > 0) out.slugAliases = aliases.map((a) => ({ slug: a.slug, until: iso(a.until) }));
    const photos = (row.brand_photos as Array<{ id: string; storage_path: string; alt?: string | null; sort_order: number }> | undefined) ?? [];
    const brand = { ...(out.brand as Record<string, unknown>) };
    if (photos.length > 0) {
      brand.photos = [...photos].sort((a, b) => a.sort_order - b.sort_order).map((p) => ({
        id: p.id, dataUrl: ctx.photoUrl ? ctx.photoUrl(p.storage_path) : p.storage_path, order: p.sort_order, ...(p.alt ? { alt: p.alt } : {}),
      }));
    }
    out.brand = brand;
    if (row.deletion_requested_at) {
      out.deletion = {
        requestedAt: iso(row.deletion_requested_at), scheduledFor: iso(row.deletion_scheduled_for),
        cancelledAppointments: Number(row.deletion_cancelled_appointments ?? 0), notifiedClients: Number(row.deletion_notified_clients ?? 0),
      };
    }
  }
  return out as unknown as EntityMap[E];
}

/**
 * Entity -> row for apply_batch. Brand photos are NOT written here (they live in Storage + brand_photos; see storage.ts) and
 * slug aliases are reconciled separately, so both are stripped from the business row.
 */
export function toRow<E extends EntityName>(entity: E, obj: EntityMap[E], ctx: MapCtx): Row {
  const e = obj as unknown as Record<string, unknown>;
  const row: Row = {};
  if (entity === 'consents') throw new Error('consents are written with consentEvents()');
  for (const s of specOf(entity)) {
    if (s.srv && ctx.mode === 'client') continue;
    if (entity === 'businesses' && s.k === 'businessId') continue;
    let v = e[s.k];
    if (entity === 'businesses' && s.k === 'brand') { const { photos: _p, ...rest } = (v ?? {}) as Record<string, unknown>; v = rest; }
    if (v === undefined) { if (s.opt) row[s.c] = 'def' in s ? s.def : null; continue; }
    row[s.c] = v;
  }
  if (entity !== 'businesses') row.business_id = e.businessId;
  const tok = TOKEN_ENTITIES[entity];
  if (tok && ctx.tokens && typeof e[tok.key] === 'string' && e[tok.key] !== '') {
    const sealed = ctx.tokens.seal(e[tok.key] as string);
    row[tok.hash] = sealed.hash;
    row[tok.enc] = sealed.enc;
  }
  if (entity === 'businesses' && ctx.mode === 'service') {
    const d = (e.deletion ?? undefined) as { requestedAt: string; scheduledFor: string; cancelledAppointments: number; notifiedClients: number } | undefined;
    row.deletion_requested_at = d?.requestedAt ?? null;
    row.deletion_scheduled_for = d?.scheduledFor ?? null;
    row.deletion_cancelled_appointments = d?.cancelledAppointments ?? 0;
    row.deletion_notified_clients = d?.notifiedClients ?? 0;
  }
  return row;
}

/** Append-only ledger events for a ConsentRecord: a grant, plus a revoke when the record is revoked. Re-sending is idempotent in SQL. */
export function consentEvents(c: ConsentRecord): Row[] {
  const base = { consent_id: c.id, business_id: c.businessId, client_id: c.clientId, channel: c.channel, purpose: c.purpose, wording_version: c.wordingVersion };
  const events: Row[] = [{ ...base, kind: 'grant', source: c.source, occurred_at: c.grantedAt }];
  if (c.revokedAt) events.push({ ...base, kind: 'revoke', source: c.revokedSource ?? c.source, occurred_at: c.revokedAt });
  return events;
}

export interface Query {
  eq: Record<string, string | number | boolean>;
  gte: Record<string, string | number>;
  lt: Record<string, string | number>;
  /** Part of the filter PostgREST cannot express (arrays, json): re-applied in memory. */
  residual: boolean;
}

/** Translates a ListFilter into PostgREST filters. Anything not expressible stays in `residual` (caller must not push `limit` down then). */
export function toQuery<E extends EntityName>(entity: E, filter: ListFilter<EntityMap[E]> | undefined, ctx: MapCtx): Query {
  const q: Query = { eq: {}, gte: {}, lt: {}, residual: false };
  if (!filter) return q;
  const specs = specOf(entity);
  const find = (key: string): F | undefined => specs.find((s) => s.k === key);
  const colOf = (key: string): string | undefined => (entity === 'businesses' && key === 'businessId' ? 'id' : find(key)?.c);
  for (const [key, val] of Object.entries((filter.where ?? {}) as Record<string, unknown>)) {
    const tok = TOKEN_ENTITIES[entity];
    if (tok && key === tok.key) {
      if (!ctx.tokens) throw new Error(`${entity}.${key} lookups need the server token vault`);
      q.eq[tok.hash] = ctx.tokens.hash(String(val));
      continue;
    }
    const s = find(key) ?? (entity === 'businesses' && key === 'businessId' ? f('businessId', 'id') : undefined);
    const col = colOf(key);
    if (!col || !s || s.t === 'a' || s.t === 'j' || val === undefined || val === null || typeof val === 'object') { q.residual = true; continue; }
    q.eq[col] = s.t === 't' ? iso(val) : (val as string | number | boolean);
  }
  if (filter.range) {
    const col = colOf(filter.range.field);
    const s = find(filter.range.field);
    if (!col || !s) q.residual = true;
    else {
      const conv = (v: string | number): string | number => (s.t === 't' ? iso(v) : v);
      if (filter.range.gte !== undefined) q.gte[col] = conv(filter.range.gte);
      if (filter.range.lt !== undefined) q.lt[col] = conv(filter.range.lt);
    }
  }
  if (filter.filter) q.residual = true;
  return q;
}

/** Row ids and business id of a mapped entity, for apply_batch bookkeeping. */
export const businessIdOf = (entity: EntityName, obj: { id: string; businessId?: string }): string => (entity === 'businesses' ? obj.id : (obj.businessId as string));
